// Which Postgres schema the audio recorder reads and writes the chart row in.
//
// The recorder popup appends to `more_audios` / `transcripts` on ONE row
// (`?id=eq.<uuid>`) and uploads the file into `{schema}/{year}/{date_}/`, so
// the only correct schema is the one the OPEN CHART ROW lives in.
//
// The viewer's HOME schema is not that schema whenever they open a chart
// through the cross-schema Providers dropdown: a STUDENT user viewing a
// SOC_WORK row still has `supabaseSchema === 'STUDENT'` (it is parsed from
// their own `users.schema` and deliberately never reassigned). Rows reached
// that way carry their own `_schema` tag — stamped by usePatientDocument and
// the schema fan-outs — and it has to outrank the ambient schema. Preferring
// the ambient one sent the PATCH to a uuid that does not exist in the home
// schema: it matched zero rows and the save died on "Patient record not
// found", which is exactly how cross-schema recordings went missing.
//
// "public" means "not set" at every level here. Real public-schema users still
// land on it, via the `|| "public"` fallback at each point of use.

/** First comma-segment of a schema string, or null when blank / "public". */
export function normalizeSchemaName(value) {
  const first = String(value ?? "")
    .split(",")[0]
    .trim();
  return first && first !== "public" ? first : null;
}

/**
 * The row a recorder `state` describes.
 *
 * Callers are inconsistent by history: MedStudentClinicPage syncs the row
 * object itself, while Schedules dispatches `patientData: [row]`. Both shapes
 * reach the same components, so both are unwrapped here.
 */
export function recorderStateRow(state) {
  return (Array.isArray(state) ? state[0] : state) || null;
}

/**
 * Schema the recorder should target, or "" when nothing resolves.
 *
 * Order, strongest first:
 *   1. `chartSchema`   — what the open chart already writes everything else
 *                        with (MedStudentClinicPage's `chartDocSchema`). It is
 *                        the row's `_schema` after the link allow-list gate,
 *                        so a revoked provider link collapses to home here too.
 *   2. the row's own `_schema` tag, for callers with no chart context.
 *   3. `dbSchemaName`  — explicitly passed by a host component.
 *   4. `homeSchema`    — the viewer's own schema; correct for same-schema work.
 *   5. `state.schema`  — legacy field some callers still set.
 */
export function resolveRecorderSchema({
  chartSchema,
  state,
  dbSchemaName,
  homeSchema,
} = {}) {
  const row = recorderStateRow(state);
  return (
    normalizeSchemaName(chartSchema) ||
    normalizeSchemaName(row?._schema) ||
    normalizeSchemaName(dbSchemaName) ||
    normalizeSchemaName(homeSchema) ||
    normalizeSchemaName(row?.schema) ||
    ""
  );
}

/**
 * Every schema the chart row may live in, best guess first. The popup looks
 * the row id up in each, in order, and saves into the first one that has it.
 *
 * `resolveRecorderSchema` reads "public" as "not set", and that's wrong for
 * a chart that really IS a public row. A STUDENT viewer recording on one was
 * sent to STUDENT."notes-c", matched nothing, and Stop & Save failed with
 * "No row id=… in STUDENT.notes-c" after the audio had already uploaded. So
 * this list returns the resolver's answer first (nothing changes when the row
 * is there), then the tags it set aside.
 *
 * A `chartSchema` has already been through the provider allow-list, so it
 * bounds the list. Adding the row's raw `_schema` would let a revoked link
 * write again.
 */
export function recorderSchemaCandidates({
  chartSchema,
  state,
  dbSchemaName,
  homeSchema,
} = {}) {
  const row = recorderStateRow(state);
  const first = (value) => String(value ?? "").split(",")[0].trim();
  const resolved =
    resolveRecorderSchema({ chartSchema, state, dbSchemaName, homeSchema }) ||
    "public";
  const rest = first(chartSchema)
    ? [first(chartSchema)]
    : [
        first(row?._schema),
        first(dbSchemaName),
        first(homeSchema),
        first(row?.schema),
        "public",
      ];
  return [...new Set([resolved, ...rest].filter(Boolean))];
}
